import { SelectChangeEvent } from "@mui/material";
import { createContext, FC, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deletePlanById,
  getAllOrganizations,
  getAllPlans,
  getAllPlanTypes,
  postPlan,
  putPlanById,
} from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { PlanContextProps } from "../../interfaces/SuperAdmin/ContextProps/iPlanes";
import { iDataTiposPlan } from "../../interfaces/SuperAdmin/Others/iDataTipoPlanes";
import { iDataOrganizacion } from "../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import {
  iDataPlan,
  PlansTypes,
  PostPlanFields,
  PutPlanFields,
} from "../../interfaces/SuperAdmin/Pages/iDataPlan";

const PlanContext = createContext<PlanContextProps>({} as PlanContextProps);

interface Props {
  children: React.ReactNode;
}

const PlanProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();
  const { canAccess } = useAuthContext();

  const [orgs, setOrgs] = useState<iDataOrganizacion[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
    idOrganization
  );

  const [planes, setPlanes] = useState<iDataPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<iDataPlan | null>(null);
  const [searchText, setSearchText] = useState("");

  const [tiposPlan, setTiposPlan] = useState<iDataTiposPlan[]>([]);

  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isLoadingTipoPlans, setIsLoadingTipoPlans] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [toDeletePlan, setToDeletePlan] = useState("");

  const UNIQUE_ID = "202cb962ac59075b964b07152d234b70";

  const navigate = useNavigate();

  // Fetch de organizaciones para seleccionar
  const fetchOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const orgsRes = await getAllOrganizations();
      if (orgsRes.data.statusCode === 200) {
        // Creamos la opción 'Todos'
        const newOption = {
          id_organizacion: UNIQUE_ID,
          id_pais: "",
          pais: "",
          nombre: "Todos",
          id_tipo_plan: "",
          tipo_plan: "",
          id_estado: "",
          estado: "",
          created_at: "",
          updated_at: "",
        };

        // Agregamos la opción 'Todos' a las organizaciones
        const newArray: iDataOrganizacion[] = [
          newOption as iDataOrganizacion,
          ...orgsRes.data.body,
        ];

        setOrgs(newArray);

        if (!idOrganization) setSelectedOrg(newOption.id_organizacion);
      } else {
        toast.error("Error al obtener las organizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las organizaciones");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  // Lo hacemos al montar el componente SOLO si es un SuperAdmin
  useEffect(() => {
    const isSuperAdmin = canAccess("view:plans");
    if (isSuperAdmin) fetchOrgs();
  }, []);

  // Funcion para cambiar de organización
  const handleSelectChange = async (event: SelectChangeEvent) => {
    setSelectedOrg(event.target.value);
    if (event.target.value === UNIQUE_ID) {
      return navigate(`/asistente/plans`);
    }
    navigate(`/asistente/plans/${event.target.value}`);
  };

  // Fetch para traer los planes de la organizacións seleccionada
  useEffect(() => {
    const fetchPlans = async (id: string | undefined) => {
      setIsLoadingPlans(true);
      try {
        const planesRes = await getAllPlans(undefined, id);
        console.log(planesRes);

        if (planesRes.data.statusCode === 200) {
          setPlanes(planesRes.data.body);
        } else {
          toast.error("Error al obtener los planes");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los planes");
      } finally {
        setIsLoadingPlans(false);
      }
    };
    if (selectedOrg) {
      fetchPlans(idOrganization);
    }
  }, [selectedOrg]);

  const fetchTipoPlanes = async () => {
    setIsLoadingTipoPlans(true);
    try {
      const tiposRes = await getAllPlanTypes();

      if (tiposRes.data.statusCode === 200) {
        const newArray = tiposRes.data.body.filter(
          (item) => item.nombre !== PlansTypes.TODOS
        );
        setTiposPlan(newArray);
      } else {
        toast.error("Error al obtener los tipos de plan");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los tipos de plan");
    } finally {
      setIsLoadingTipoPlans(false);
    }
  };

  const toggleCreateModal = (isOpen: boolean) => {
    if (isOpen) {
      fetchTipoPlanes();
    }
    setIsCreateModalOpen(isOpen);
  };

  const toggleEditModal = (isOpen: boolean) => {
    if (isOpen) {
      fetchTipoPlanes();
    }
    if (!isOpen) {
      setSelectedPlan(null);
    }
    setIsEditModalOpen(isOpen);
  };

  const handleEdit = (plan: iDataPlan) => {
    setSelectedPlan(plan);
    toggleEditModal(true);
  };

  const assignToDeletePlan = (planId: string) => {
    setToDeletePlan(planId);
  };

  const handleDelete = async () => {
    if (toDeletePlan) {
      setIsDeleting(true);
      try {
        const res = await deletePlanById(toDeletePlan);
        console.log(res);

        if (res.data.statusCode === 200) {
          setPlanes((prev) => {
            const filteredPlanes = prev.filter(
              (plan) => plan.id_plan !== toDeletePlan
            );
            return filteredPlanes;
          });
          setToDeletePlan("");
          toast.success("Plan eliminado exitosamente");
        } else {
          toast.error("Error al elimiar el plan");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al elimiar el plan");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const planCreate = async (data: PostPlanFields) => {
    setIsCreating(true);
    try {
      const res = await postPlan(data);
      console.log(res);

      if (res.data.statusCode === 201) {
        setPlanes((prev) => [...prev, res.data.body]);
        toggleCreateModal(false);
        toast.success("Plan creado exitosamente");
      } else {
        toast.error("Error al crear el plan");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al crear el plan");
    } finally {
      setIsCreating(false);
    }
  };

  const planEdit = async (id_plan: string, data: PutPlanFields) => {
    setIsEditing(true);
    try {
      const res = await putPlanById(id_plan, data);

      if (res.data.statusCode === 200) {
        setPlanes((prev) =>
          prev.map((plan) => {
            if (plan.id_plan === id_plan) {
              return res.data.body;
            }
            return plan;
          })
        );
        toggleEditModal(false);
        toast.success("Plan editado exitosamente");
      } else {
        toast.error("Error al editar el plan");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar el plan");
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <PlanContext.Provider
      value={{
        planes,
        selectedPlan,
        orgs,
        searchText,
        selectedOrg,
        tiposPlan,
        toDeletePlan,
        isLoadingPlans,
        isLoadingOrgs,
        isLoadingTipoPlans,
        isCreating,
        isEditing,
        isDeleting,
        isCreateModalOpen,
        isEditModalOpen,
        toggleCreateModal,
        toggleEditModal,
        handleEdit,
        handleDelete,
        assignToDeletePlan,
        handleSelectChange,
        planCreate,
        planEdit,
        setSearchText,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
};

const useSuperAdminPlans = () => useContext(PlanContext);

export { PlanProvider, useSuperAdminPlans };
